import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { toast } from 'react-toastify';
import { CircularProgress } from '@material-ui/core';
import { firebaseAuthFunc } from '../../config/firebaseAuth';
import { doc, getDoc } from 'firebase/firestore';
import { firebaseBigFireStore } from '../../config/firebaseFireBig';

const DERIVATION_PATHS = {
  main: "m/44'/0'/0'/0",
  escrow: "m/43'/0'/0'/0/0",
};

const WALLET_ID = '00000000-0000-0000-0000-000000000000';

export default function WalletRecoverySection() {
  const [mnemonic, setMnemonic] = useState(null);
  const [loading, setLoading] = useState(false);
  const [revealed, setRevealed] = useState(false);

  const userWalletRedux = useSelector((state) => state.walletReducer);

  const fetchMnemonic = async () => {
    setLoading(true);
    try {
      // First try to get from Redux (if still available from Relysia)
      if (userWalletRedux?.mnemonic) {
        setMnemonic(userWalletRedux.mnemonic);
        setRevealed(true);
        setLoading(false);
        return;
      }

      // Otherwise, fetch from Firestore and decrypt
      const user = firebaseAuthFunc.currentUser;
      if (!user) {
        toast.error('Please sign in to retrieve your mnemonic');
        setLoading(false);
        return;
      }

      // Fetch encrypted mnemonic from Firestore
      const walletDocRef = doc(
        firebaseBigFireStore,
        'keyStore',
        user.uid,
        'wallets',
        WALLET_ID
      );
      const walletDoc = await getDoc(walletDocRef);

      if (!walletDoc.exists()) {
        toast.error('No wallet found for this account');
        setLoading(false);
        return;
      }

      const encryptedData = walletDoc.data();

      const idToken = await user.getIdToken();
      const response = await fetch('/api/decrypt-mnemonic', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          walletId: WALLET_ID,
          encryptedData: encryptedData,
        }),
      });

      const data = await response.json();

      if (data.status === 'success' && data.mnemonic) {
        setMnemonic(data.mnemonic);
        setRevealed(true);
      } else {
        toast.error(data.error || 'Failed to retrieve mnemonic');
      }
    } catch (error) {
      console.error('Error fetching mnemonic:', error);
      toast.error('Failed to retrieve mnemonic. Please try again.');
    }
    setLoading(false);
  };

  const copyToast = (text) => {
    toast.success(`${text} copied to clipboard`, {
      position: 'bottom-left',
      autoClose: 2000,
    });
  };

  return (
    <div
      style={{
        backgroundColor: '#f8f9fa',
        borderRadius: '12px',
        padding: '24px',
        marginTop: '20px',
        border: '1px solid #e9ecef',
      }}
    >
      <div style={{ textAlign: 'center', marginBottom: '20px' }}>
        <h3
          style={{
            fontSize: '18px',
            fontWeight: '600',
            color: '#1a1a2e',
            marginBottom: '8px',
          }}
        >
          Export Your Wallet
        </h3>
        <p style={{ color: '#6c757d', fontSize: '14px' }}>
          Save your recovery phrase to access your funds in any BSV wallet
        </p>
      </div>

      {/* Important Notice */}
      <div
        style={{
          backgroundColor: '#fff3cd',
          border: '1px solid #ffc107',
          borderRadius: '8px',
          padding: '16px',
          marginBottom: '20px',
        }}
      >
        <p style={{ fontSize: '13px', color: '#856404', margin: 0 }}>
          <strong>Important:</strong> Your recovery phrase (mnemonic) gives full
          access to your wallet. Store it securely offline and never share it
          with anyone.
        </p>
      </div>

      {/* Mnemonic Section */}
      {!revealed ? (
        <div style={{ textAlign: 'center' }}>
          <button
            onClick={fetchMnemonic}
            disabled={loading}
            style={{
              backgroundColor: '#1a1a2e',
              color: 'white',
              padding: '12px 24px',
              borderRadius: '8px',
              border: 'none',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '500',
            }}
          >
            {loading ? (
              <>
                <CircularProgress size={16} color="inherit" style={{ marginRight: 8 }} />
                Retrieving...
              </>
            ) : (
              'Reveal Recovery Phrase'
            )}
          </button>
        </div>
      ) : (
        <>
          {/* Mnemonic Display */}
          <div
            style={{
              backgroundColor: '#fff',
              border: '2px solid #1a1a2e',
              borderRadius: '8px',
              padding: '16px',
              marginBottom: '16px',
            }}
          >
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: '8px',
              }}
            >
              {mnemonic?.split(' ').map((word, index) => (
                <div
                  key={index}
                  style={{
                    backgroundColor: '#f8f9fa',
                    padding: '8px',
                    borderRadius: '4px',
                    fontSize: '13px',
                  }}
                >
                  <span style={{ color: '#6c757d', marginRight: '4px' }}>
                    {index + 1}.
                  </span>
                  <span style={{ fontWeight: '500' }}>{word}</span>
                </div>
              ))}
            </div>
            <div style={{ marginTop: '12px', textAlign: 'center' }}>
              <CopyToClipboard
                text={mnemonic}
                onCopy={() => copyToast('Recovery phrase')}
              >
                <button
                  style={{
                    backgroundColor: '#1a1a2e',
                    color: 'white',
                    padding: '8px 16px',
                    borderRadius: '6px',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '13px',
                  }}
                >
                  Copy Recovery Phrase
                </button>
              </CopyToClipboard>
            </div>
          </div>

          {/* Derivation Paths */}
          <div
            style={{
              backgroundColor: '#e7f5ff',
              border: '1px solid #74c0fc',
              borderRadius: '8px',
              padding: '16px',
            }}
          >
            <h4
              style={{
                fontSize: '14px',
                fontWeight: '600',
                marginBottom: '12px',
                color: '#1971c2',
              }}
            >
              Derivation Paths
            </h4>
            <p style={{ fontSize: '13px', color: '#495057', marginBottom: '12px' }}>
              Use these paths when importing your wallet to access all your funds:
            </p>

            <div style={{ marginBottom: '12px' }}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  backgroundColor: '#fff',
                  padding: '10px 12px',
                  borderRadius: '6px',
                  marginBottom: '8px',
                }}
              >
                <div>
                  <strong style={{ fontSize: '13px' }}>Main Wallet (BSV)</strong>
                  <code
                    style={{
                      display: 'block',
                      fontSize: '12px',
                      color: '#6c757d',
                      marginTop: '2px',
                    }}
                  >
                    {DERIVATION_PATHS.main}/0
                  </code>
                </div>
                <CopyToClipboard
                  text={DERIVATION_PATHS.main}
                  onCopy={() => copyToast('Main path')}
                >
                  <button
                    style={{
                      background: 'none',
                      border: '1px solid #dee2e6',
                      borderRadius: '4px',
                      padding: '4px 8px',
                      cursor: 'pointer',
                      fontSize: '12px',
                    }}
                  >
                    Copy
                  </button>
                </CopyToClipboard>
              </div>

              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  backgroundColor: '#fff',
                  padding: '10px 12px',
                  borderRadius: '6px',
                }}
              >
                <div>
                  <strong style={{ fontSize: '13px' }}>NFT Escrow Address</strong>
                  <code
                    style={{
                      display: 'block',
                      fontSize: '12px',
                      color: '#6c757d',
                      marginTop: '2px',
                    }}
                  >
                    {DERIVATION_PATHS.escrow}
                  </code>
                  <span style={{ fontSize: '11px', color: '#868e96' }}>
                    (NFTs that were listed for sale)
                  </span>
                </div>
                <CopyToClipboard
                  text={DERIVATION_PATHS.escrow}
                  onCopy={() => copyToast('Escrow path')}
                >
                  <button
                    style={{
                      background: 'none',
                      border: '1px solid #dee2e6',
                      borderRadius: '4px',
                      padding: '4px 8px',
                      cursor: 'pointer',
                      fontSize: '12px',
                    }}
                  >
                    Copy
                  </button>
                </CopyToClipboard>
              </div>
            </div>

            <p style={{ fontSize: '12px', color: '#495057', margin: 0 }}>
              <strong>Compatible wallets:</strong> ElectrumSV, or any BSV wallet that
              supports custom derivation paths and STAS tokens.
            </p>
          </div>
        </>
      )}
    </div>
  );
}
